<template>
  <v-text-field
      rounded="lg"
      :error-messages="error"
      :label="translation(translationList.house_number_of_rooms, 'Număr camere')"
      v-model="numberOfRoomsString"
      name="number_of_rooms"
      :rules="numberOfRoomsValidationRules"
      @input="onInput"
      variant="outlined"
      bg-color="surface"
      :tile="true"
      type="number"
  >
  </v-text-field>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, toRefs } from 'vue';
import { useTranslationStore, translation } from '~/plugins/store/translations';
import type {TranslationList} from "~/types/Utils";

export default defineComponent({
  props: {
    modelValue: Number as () => number | null,
  },
  setup(props, { emit }) {
    const { modelValue } = toRefs(props);
    const numberOfRooms = ref<number | null>(modelValue.value ?? null);
    const numberOfRoomsString = ref<string | null>(modelValue.value !== null ? String(modelValue.value) : null);
    const error = ref<string[]>([]);

    watch(numberOfRooms, (newValue) => {
      const isValid = validateNumberOfRooms(newValue);
      if (isValid) {
        emit('update:modelValue', newValue);
      } else {
        emit('update:modelValue', null);
      }
      emit('validation-house-status', { field: 'number_of_rooms', isValid });
    });

    watch(numberOfRoomsString, (newValue) => {
      const parsedValue = parseFloat(newValue as string);
      if (!isNaN(parsedValue)) {
        numberOfRooms.value = parsedValue;
      } else {
        numberOfRooms.value = null;
      }
    });

    function validateNumberOfRooms(value: number | null, showErrors?: boolean): boolean {
      error.value = [];
      if (value !== null) {
        if (showErrors) {
          const errors = numberOfRoomsValidationRules
              .map(rule => rule(value))
              .filter(result => result !== true) as string[];
          if (errors.length > 0) {
            error.value = errors;
            return false;
          }
        }
        return numberOfRoomsValidationRules.every(rule => rule(value) === true);
      } else if (showErrors) {
        error.value.push(translation(translationList.value.required, 'Acest câmp este obligatoriu'));
        return false;
      }
      return false;
    }

    const translationList = ref<TranslationList>({});
    const translationStore = useTranslationStore();

    const numberOfRoomsValidationRules = [
      (v: number) => !!v || translation(translationList.value.required, 'Acest câmp este obligatoriu'),
      (v: number) => v >= 0 || translation(translationList.value.invalid_field, 'Acest câmp este invalid.'),
    ];

    const onInput = (event: Event) => {
      const input = event.target as HTMLInputElement;
      numberOfRoomsString.value = input.value;
    };

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');

      // Listen for validate event
      window.addEventListener('validate-house', (data) => {
        if (data.detail && data.detail === 'number_of_rooms') {
          const isValid = validateNumberOfRooms(numberOfRooms.value, true);
          if (isValid) {
            emit('validation-house-status', { field: 'number_of_rooms', isValid });
          }
        }
      });
    });

    watch(modelValue, (newValue) => {
      const formattedValue = newValue !== null ? String(newValue) : null;
      if (formattedValue !== numberOfRoomsString.value) {
        numberOfRoomsString.value = formattedValue;
      }
    });

    return {
      numberOfRooms,
      numberOfRoomsString,
      numberOfRoomsValidationRules,
      translationList,
      translation,
      onInput,
      error,
    };
  },
});
</script>